import { Col, Row, Space } from 'antd';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash/fp';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { type ConditionSettings, type TextConsentsAndDeclarationsSettings } from '../../../api/types';
import TranslatedTextAreaField from '../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../themes/admin/Fields/FormFields';
import useConsentsAndDeclarationsOptions from '../../../utilities/useConsentsAndDeclarationsOptions';
import useDataFieldOptions from '../../../utilities/useDataFieldOptions';
import useSystemSwitchData from '../../../utilities/useSystemSwitchData';
import ConditionsDisplay from './Conditions/ConditionsDisplay';

export type TextConsentFormValues = TextConsentsAndDeclarationsSettings & {
    conditionSettings: ConditionSettings[];
};

export type TextConsentsAndDeclarationsDetailsProps = {
    initialValues: TextConsentFormValues;
    updated?: string;
    moduleName: string;
    offset?: string;
    disabled?: boolean;
};

const colSpan = { lg: 8, md: 12, xs: 24 };

const TextConsentsAndDeclarationsDetails = ({
    updated,
    moduleName,
    offset,
    disabled = false,
}: TextConsentsAndDeclarationsDetailsProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const { values, setFieldValue } = useFormikContext<TextConsentFormValues>();
    const { yesNoSwitch } = useSystemSwitchData();
    const { purposeOptions } = useConsentsAndDeclarationsOptions();
    const dataFieldOptions = useDataFieldOptions();
    useEffect(() => {
        setFieldValue('isMandatory', undefined);
        setFieldValue('legalMarkup', undefined);
        setFieldValue('legalTextPosition', undefined);
    }, [setFieldValue]);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('consentsAndDeclarations:cardTitles.configuration')}>
                <Panel
                    key={t('consentsAndDeclarations:cardTitles.configuration')}
                    header={t('consentsAndDeclarations:cardTitles.configuration')}
                >
                    <Row gutter={14}>
                        <Col {...colSpan}>
                            <FormFields.InputField
                                {...t('consentsAndDeclarations:fields.displayName', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="displayName"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.TranslatedInputField
                                {...t('consentsAndDeclarations:fields.title', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="title"
                                required={
                                    isEmpty(values.description.defaultValue) || values.description.defaultValue === ''
                                }
                            />
                        </Col>
                        {moduleName && (
                            <Col {...colSpan}>
                                <FormFields.DisplayField
                                    {...t('consentsAndDeclarations:fields.moduleName', {
                                        returnObjects: true,
                                    })}
                                    value={moduleName}
                                    disabled
                                />
                            </Col>
                        )}
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('consentsAndDeclarations:fields.placement', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                mode="multiple"
                                name="purpose"
                                options={purposeOptions}
                                required
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SelectField
                                {...t('consentsAndDeclarations:fields.dataField', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="dataField"
                                options={dataFieldOptions}
                                required
                                showSearch
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.SwitchField
                                {...t('consentsAndDeclarations:fields.isActive', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="isActive"
                                {...yesNoSwitch}
                            />
                        </Col>
                        <Col {...colSpan}>
                            <FormFields.InputNumberField
                                {...t('consentsAndDeclarations:fields.orderNumber', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                min={1}
                                name="orderNumber"
                                required
                            />
                        </Col>
                        <Col {...colSpan}>
                            <TranslatedTextAreaField
                                {...t('consentsAndDeclarations:fields.description', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name="description"
                                required={isEmpty(values.title.defaultValue) || values.title.defaultValue === ''}
                            />
                        </Col>
                        {updated && (
                            <Col {...colSpan}>
                                <FormFields.DisplayFieldWithUTCOffset
                                    {...t('common:fields.updated', { returnObjects: true, offset })}
                                    value={updated}
                                />
                            </Col>
                        )}
                    </Row>
                </Panel>
            </CollapsibleWrapper>

            <CollapsibleWrapper defaultActiveKey={t('consentsAndDeclarations:cardTitles.conditions')}>
                <Panel
                    key={t('consentsAndDeclarations:cardTitles.conditions')}
                    header={t('consentsAndDeclarations:cardTitles.conditions')}
                >
                    <ConditionsDisplay disabled={disabled} name="conditionSettings" />
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default TextConsentsAndDeclarationsDetails;
